import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getAge } from "@/utils/dateUtil";
import { DetailedCitizenAttendancesIndicatorC4Data } from "@/services/esus/indicator/esf/c4/getDetailedCitizensAttendancesIndicatorC4";
import { getCitizenScoreStatusDescriptionIndicatorC4 } from "@/utils/indicatorsEsf";

import { IndicatorEsfC4CitizensAttendancesFilters } from "./IndicatorEsfC4CitizensAttendancesFilters";
import { IndicatorEsfC4CitizensAttendancesTable } from "./IndicatorEsfC4CitizensAttendancesTable";
import { IndicatorEsfC4CitizenAttendancesHistory } from "./IndicatorEsfC4CitizenAttendancesHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC4CitizensAttendancesProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedAttendances: Paginated<DetailedCitizenAttendancesIndicatorC4Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllDetailedIndicatorC4: () => Promise<DetailedCitizenAttendancesIndicatorC4Data[]>;
};

export function IndicatorEsfC4CitizensAttendances({
	selectedQuarter,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedAttendances,
	pageNumber,
	setPageNumber,
	handleGetAllDetailedIndicatorC4: handleGetAllDetailedIndicatorC1,
}: Readonly<IndicatorEsfC4CitizensAttendancesProps>) {
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [citizenWithAttendances, setCitizenWithAttendances] =
		useState<DetailedCitizenAttendancesIndicatorC4Data>(
			new DetailedCitizenAttendancesIndicatorC4Data()
		);
	const [isLoadingCitizenWithAttendances, setIsLoadingCitizenWithAttendances] = useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithAttendances(
		citizenWithAttendances: DetailedCitizenAttendancesIndicatorC4Data
	) {
		setIsLoadingCitizenWithAttendances(true);
		setCitizenWithAttendances(citizenWithAttendances);
		setIsLoadingCitizenWithAttendances(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const attendances = await handleGetAllDetailedIndicatorC1();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(attendances).length > 0) {
			const excelData: GenericObject[] =
				transformIndicatorC1AttendancesToGenericObjectArray(attendances);
			exportExcel(
				excelData,
				`Atendimentos - ${dayjs(quarterDates.startDate).format("DD-MM-YYYY")} à ${dayjs(
					quarterDates.endDate
				).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorC1AttendancesToGenericObjectArray(
		citizensWithAttendances: DetailedCitizenAttendancesIndicatorC4Data[]
	) {
		return citizensWithAttendances.map(
			(citizenWithAttendances) =>
				({
					Nome: citizenWithAttendances.fullName,
					DN: citizenWithAttendances.birthdate
						? dayjs(citizenWithAttendances.birthdate).format("DD/MM/YYYY")
						: "",
					Idade: citizenWithAttendances.birthdate
						? getAge(citizenWithAttendances.birthdate)
						: "",
					Documento: citizenWithAttendances.cpf ?? citizenWithAttendances.cns ?? "",
					Area: citizenWithAttendances.area ?? "FA",
					"Ine Equipe": citizenWithAttendances.healthTeam?.ine ?? "",
					"Nome Equipe": citizenWithAttendances.healthTeam?.name ?? "",
					"Última Consulta": citizenWithAttendances.lastAttendance?.date
						? dayjs(citizenWithAttendances.lastAttendance.date).format("DD/MM/YYYY")
						: "",
					"Status última Consulta": getCitizenScoreStatusDescriptionIndicatorC4(
						citizenWithAttendances.lastAttendanceStatus
					),
					"Última aferição PA": citizenWithAttendances.bloodPressureMeasurements?.at(0)
						? dayjs(
								citizenWithAttendances.bloodPressureMeasurements.at(0)?.date
						  ).format("DD/MM/YYYY")
						: "",
					"Status última aferição PA": getCitizenScoreStatusDescriptionIndicatorC4(
						citizenWithAttendances.lastBloodPressureMeasurementStatus
					),
					"Último registro peso e altura":
						citizenWithAttendances.weightAndHeightMetricsRegisters?.at(0)
							? dayjs(
									citizenWithAttendances.weightAndHeightMetricsRegisters.at(0)
										?.date
							  ).format("DD/MM/YYYY")
							: "",
					"Status último registro peso e altura":
						getCitizenScoreStatusDescriptionIndicatorC4(
							citizenWithAttendances.lastWeightAndHeightMetricsRegisterStatus
						),
					"Último registro hemoglobina glicada":
						citizenWithAttendances.glycatedHemoglobinAttendanceProceduresReqEval?.at(0)
							? dayjs(
									citizenWithAttendances.glycatedHemoglobinAttendanceProceduresReqEval.at(
										0
									)?.date
							  ).format("DD/MM/YYYY")
							: "",
					"Status último registro hemoglobina glicada":
						getCitizenScoreStatusDescriptionIndicatorC4(
							citizenWithAttendances.lastGlycatedHemoglobinStatus
						),
					"Último exame dos pés":
						citizenWithAttendances.footAssessmentAttendanceProceduresReqEval?.at(0)
							? dayjs(
									citizenWithAttendances.footAssessmentAttendanceProceduresReqEval.at(
										0
									)?.date
							  ).format("DD/MM/YYYY")
							: "",
					"Status último exame dos pés": getCitizenScoreStatusDescriptionIndicatorC4(
						citizenWithAttendances.lastFootAssessmentStatus
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingCitizenWithAttendances || isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC4CitizensAttendancesFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC4CitizensAttendancesTable
							citizensWithAttendances={paginatedAttendances.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithAttendances}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedAttendances?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedAttendances.pageSize}
								totalPages={paginatedAttendances.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Histórico de Atendimentos
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEsfC4CitizenAttendancesHistory
							citizenWithAttendances={citizenWithAttendances}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
